/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import DiagramSymbolMindMapIcon from '@atlaskit/icon-lab/core/diagram-symbol-mind-map';
import StickerIcon from '@atlaskit/icon-lab/core/sticker';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { BUILTIN_TOOLBAR_KEYS } from '@atlaskit/native-embeds-common/constants';
import type { ManifestEditorToolbarActions } from '@atlaskit/native-embeds-common/types';
import { createEditorToolbarActions } from '@atlaskit/native-embeds-common/utils';
import { WHITEBOARD_VIEWPORT_ALIGNMENT_FEATURE } from '@atlaskit/native-embeds-common/whiteboard-viewport-alignment';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
import { getInitialThemeParam } from '@atlassian/native-embeds-core/theme';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';
import {
	type QueryParams,
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
} from '@atlassian/native-embeds-core/types/experience/types';
import {
	allNativeEmbedCoreFeatures,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';
import type {
	GeneratingOverlayHold,
	GeneratingOverlayHoldContext,
	LoadingComponentProps,
} from '@atlassian/native-embeds-core/types/ui-config';

export const MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID: ExperienceManifestId =
	experienceManifestId('maui-whiteboard');

/**
 * Dynamic-config killswitch for sending `disableCanvasScroll` conditionally in
 * legacy full screen. Default `false` = change enabled (param omitted from the
 * legacy full-screen URL so the blown-up board scrolls); set to `true` to roll
 * back to the pre-EPS8-40 behavior (param sent everywhere).
 */
const LEGACY_FS_CANVAS_SCROLL_KILLSWITCH = 'platform_native_embed_legacy_fs_canvas_scroll';
const ROVO_REMIX_TITLE_KILLSWITCH = 'platform_native_embed_rovo_remix_title';
const ROVO_STREAMING_GENERATION_QUERY_PARAM = 'rovoStreamingGeneration';
const REMIX_WHITEBOARD_LOADING_CONFIG = 'platform_remix_whiteboard_loading';
const ROVO_STREAMING_RECEIVER_QUERY_PARAM = 'rovoReceiverId';

const GENERATING_OVERLAY_FAIL_OPEN_MS = 60_000;
const RELATIVE_URL_PARSE_BASE = 'https://native-embeds.invalid';

const whiteboardIcons = [WhiteboardIcon, DiagramSymbolMindMapIcon, ShapesIcon, StickerIcon];

/**
 * Captures the whiteboard resource id from a Confluence whiteboard URL. Numeric
 * ids and UUIDs are both in use.
 */
// eslint-disable-next-line require-unicode-regexp
const WHITEBOARD_RESOURCE_ID_REGEX = /\/whiteboard\/([^/?#]+)/;

/**
 * A whiteboard embed's identity is its content id. The space segment is vanity
 * (the rendered canvas URL is `/canvas-embed/whiteboard/{referenceId}` and has no
 * space key), and the streaming params are transient, so neither changes which
 * board is being shown.
 *
 * Scoped to the MAUI experience on purpose. The in-editor Remix finalize keeps
 * `isRemixDiagram=true` on the rewritten URL, so both the streaming and finalized
 * URLs still match this manifest — which `main.tsx` registers ahead of the generic
 * whiteboard one — and identity resolves from here on both sides of the rewrite.
 *
 * The paste path is deliberately not covered: `buildMovedUrl` strips
 * `isRemixDiagram`, so its finalized URL falls through to the generic `whiteboard`
 * manifest, which has no identity and therefore keeps the existing URL-keyed
 * teardown. That path's reload is already largely masked because the move fires on
 * the tick after paste, while the board is still loading. Extending identity to
 * the generic manifest would change behaviour for every non-MAUI Confluence
 * whiteboard embed, which is not worth it for this fix.
 */
const getWhiteboardContentIdentity = (url: string): string | undefined => {
	const resourceId = WHITEBOARD_RESOURCE_ID_REGEX.exec(url)?.[1];
	if (!resourceId) {
		return undefined;
	}
	try {
		return `whiteboard:${decodeURIComponent(resourceId)}`;
	} catch {
		return `whiteboard:${resourceId}`;
	}
};

const isMauiWhiteboardUrl = (url: string): boolean => {
	const isWhiteboardPath =
		url.match(/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>\d+)(\?\/)?/) ||
		url.match(
			/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})(\?\/)?/,
		);
	if (!isWhiteboardPath) {
		return false;
	}
	try {
		return new URL(url).searchParams.get('isRemixDiagram') === 'true';
	} catch {
		return false;
	}
};

/**
 * Reads search params from either an absolute embed URL or a relative iframe
 * src such as `/canvas-embed/whiteboard/...`. The placeholder base is only
 * used by `URL` to parse relative URLs; absolute URLs keep their own origin
 * and nothing is requested from this host.
 */
const getSearchParamFromEmbedUrl = (url: string, param: string): string | null => {
	try {
		return new URL(url, RELATIVE_URL_PARSE_BASE).searchParams.get(param);
	} catch {
		return null;
	}
};

const resolveGeneratingOverlayHold = ({
	experience,
	url,
}: GeneratingOverlayHoldContext): GeneratingOverlayHold | undefined => {
	if (!UNSAFE_expValNoExposure(REMIX_WHITEBOARD_LOADING_CONFIG, 'value', false)) {
		return undefined;
	}

	const receiverId = getSearchParamFromEmbedUrl(url, ROVO_STREAMING_RECEIVER_QUERY_PARAM);
	const generationId = getSearchParamFromEmbedUrl(url, ROVO_STREAMING_GENERATION_QUERY_PARAM);
	if (!receiverId) {
		return undefined;
	}

	const holdKey = generationId ? receiverId + ':' + generationId : receiverId;
	const matchesGeneration = (event: { generationId?: string }): boolean =>
		generationId === null || event.generationId === generationId;
	const matchesContentReadyGeneration = (event: { generationId?: string }): boolean =>
		matchesGeneration(event) || (generationId !== null && event.generationId === undefined);

	return {
		key: holdKey,
		subscribeToFirstContentPaint: (start) => {
			let started = false;
			let cleanedUp = false;
			let unsubscribeFirstContentPainted: (() => void) | undefined;

			const startOnce = (): void => {
				if (started || cleanedUp) {
					return;
				}
				started = true;
				start();
			};

			unsubscribeFirstContentPainted = experience.on(
				'first-content-painted',
				(event) => {
					if (matchesGeneration(event)) {
						startOnce();
					}
				},
				{ replayLast: true },
			);

			return () => {
				cleanedUp = true;
				unsubscribeFirstContentPainted?.();
			};
		},
		subscribeToRelease: (release) => {
			let released = false;
			let cleanedUp = false;
			let failOpenTimeout: ReturnType<typeof setTimeout> | undefined;
			let unsubscribeStreamComplete: (() => void) | undefined;
			let unsubscribeContentReady: (() => void) | undefined;

			const releaseOnce = (): void => {
				if (released || cleanedUp) {
					return;
				}
				released = true;
				if (failOpenTimeout !== undefined) {
					clearTimeout(failOpenTimeout);
					failOpenTimeout = undefined;
				}
				release();
			};

			const cleanup = (): void => {
				if (cleanedUp) {
					return;
				}
				cleanedUp = true;
				if (failOpenTimeout !== undefined) {
					clearTimeout(failOpenTimeout);
					failOpenTimeout = undefined;
				}
				unsubscribeStreamComplete?.();
				unsubscribeContentReady?.();
			};

			try {
				unsubscribeStreamComplete = experience.on(
					'stream-complete',
					(event) => {
						if (matchesGeneration(event)) {
							releaseOnce();
						}
					},
					{ replayLast: true },
				);
				if (!released) {
					// Keep content-ready as a compatibility fallback for older Canvas
					// deploys that do not emit stream-complete yet.
					unsubscribeContentReady = experience.on(
						'content-ready',
						(event) => {
							if (matchesContentReadyGeneration(event)) {
								releaseOnce();
							}
						},
						{ replayLast: true },
					);
				}
				if (!released) {
					failOpenTimeout = setTimeout(() => {
						releaseOnce();
					}, GENERATING_OVERLAY_FAIL_OPEN_MS);
				}
			} catch (error) {
				cleanup();
				throw error;
			}

			return cleanup;
		},
	};
};

const shouldHideRovoRemixTitle = (url: string): boolean => {
	// Killswitch: platform_native_embed_rovo_remix_title
	// false (default) = hide intermediary Rovo remix titles; true = rollback
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	if (FeatureGates.getExperimentValue(ROVO_REMIX_TITLE_KILLSWITCH, 'value', false)) {
		return false;
	}

	return getSearchParamFromEmbedUrl(url, 'parentProduct') === 'rovo';
};

/**
 * Experience manifest for a MAUI whiteboard embed — a whiteboard created through
 * the MAUI (remix diagram) flow.
 *
 * Register with getNativeEmbedFacade().registerExperience(MauiWhiteboardExperienceManifest)
 * to obtain an Experience for EmbedFrameUI.
 */
export const MauiWhiteboardExperienceManifest: {
	readonly getContentIdentity: (url: string) => string | undefined;
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly queryParams: () => QueryParams;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: object;
		readonly headerControls: {
			readonly hideTitle: (context: { url: string }) => boolean;
		};
		readonly isAskRovoEnabled: () => boolean;
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly resolveGeneratingOverlayHold: (
			context: GeneratingOverlayHoldContext,
		) => GeneratingOverlayHold | undefined;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly trackViewed: true;
		readonly waitForContentReady: true;
	};
} = {
	id: MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID,
	getContentIdentity: getWhiteboardContentIdentity,
	isUrlMatch: isMauiWhiteboardUrl,
	supportedFeatures: new Set<NativeEmbedFeature>([
		...allNativeEmbedCoreFeatures,
		WHITEBOARD_VIEWPORT_ALIGNMENT_FEATURE,
	]),
	// Thunk so the killswitch is read at URL-build time (after Statsig init), and
	// so the conditional behavior is gated at its source: when rolled back the
	// manifest emits the pre-EPS8-40 plain-string value, which `resolveQueryParams`
	// passes through unchanged at every build site.
	//
	// `disableCanvasScroll` is correct inline (and in shared-iframe full screen,
	// which reuses the inline URL) but makes the blown-up board feel broken in the
	// legacy full-screen iframe, so by default it is omitted from that build site
	// only. `hideCursors` applies everywhere (plain string === appliesTo: 'always').
	queryParams: (): QueryParams => {
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const rolledBack = FeatureGates.getExperimentValue(
			LEGACY_FS_CANVAS_SCROLL_KILLSWITCH,
			'value',
			false,
		);
		return rolledBack
			? { disableCanvasScroll: 'true', hideCursors: 'true' }
			: {
					disableCanvasScroll: { value: 'true', appliesTo: 'exceptLegacyFullscreen' },
					hideCursors: 'true',
				};
	},
	transformUrl: (url: string, isFullScreen?: boolean): string => {
		const urlObj = new URL(url);
		if (!isFullScreen) {
			urlObj.searchParams.set('hideBorder', 'true');
		}
		// this is used to identify the native embed view in the whiteboard app
		urlObj.searchParams.set('nativeEmbedView', 'true');

		// Default the parent product for MAUI whiteboards when the native embed
		// URL doesn't already specify one. native-embeds-core re-applies this
		// onto the ORS-resolved iframe src (which otherwise defaults to
		// `smartlink`). An existing value (e.g. `rovo`) is preserved.
		if (!urlObj.searchParams.has('parentProduct')) {
			urlObj.searchParams.set('parentProduct', 'nativeEmbedDiagram');
		}

		const initialTheme = getInitialThemeParam();
		if (initialTheme) {
			urlObj.searchParams.set('themeState', initialTheme);
		}

		return urlObj.toString();
	},
	isActive: (): true => {
		return true;
	},
	uiConfig: {
		trackViewed: true,
		isAskRovoEnabled: () => fg('cc-maui-phase-2'),
		resolveGeneratingOverlayHold,
		editorToolbarActions: () =>
			createEditorToolbarActions({
				items: [
					BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
					...(fg('platform_editor_ai_remix_toolbar') ? [BUILTIN_TOOLBAR_KEYS.REMIX_VARIANT] : []),
					BUILTIN_TOOLBAR_KEYS.SUMMARISE,
					BUILTIN_TOOLBAR_KEYS.SEPARATOR,
					BUILTIN_TOOLBAR_KEYS.EMBED,
					BUILTIN_TOOLBAR_KEYS.EDIT,
					...(fg('cc-maui-toolbar-separators-update-jira') ? [] : [BUILTIN_TOOLBAR_KEYS.SEPARATOR]),
					// BUILTIN_TOOLBAR_KEYS.BORDER, // Border option is yet to be implemented.
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
					...(fg('cc-maui-toolbar-separators-update-jira')
						? []
						: [
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
								BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
								BUILTIN_TOOLBAR_KEYS.SEPARATOR,
							]),
				],
				moreItems: [
					BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
					BUILTIN_TOOLBAR_KEYS.COPY,
					BUILTIN_TOOLBAR_KEYS.DELETE,
				],
			}),
		editorToolbarHandlers: {},
		headerControls: {
			hideTitle: ({ url }) => shouldHideRovoRemixTitle(url),
		},
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => (
			<LoadingOverlay
				animationDisabled={props.animationDisabled}
				icons={whiteboardIcons}
				testId={props.testId}
			/>
		),
		titleIcon: WhiteboardIcon,
		appearance: 'overlay',
		waitForContentReady: true,
	},
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
} as const satisfies ExperienceManifest;
